import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Clock,
  DollarSign,
  Building,
  FileText,
  GraduationCap,
  Users,
  Calendar,
  Edit3,
  Trash2,
  ArrowLeft,
  Star,
  UserCheck,
  Mail,
  Phone,
  Eye,
  Briefcase
} from "lucide-react";
import api from "../../../utils/Api";

const MissionDetailsPage = () => {
  const { t } = useTranslation("createMission");
  const { id } = useParams();
  const navigate = useNavigate();

  const [mission, setMission] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch mission data on component mount
  useEffect(() => {
    const fetchMission = async () => {
      try {
        setLoading(true);
        const response = await api.get("/mission/" + id);
        setMission(response.data.data);
      } catch (error) {
        console.error("Error fetching mission:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchApplications = async () => {
      try {
        setApplicationsLoading(true);
        // Note: Check if this route is correct. Using /jobOffer/:id/applications might be wrong if it's a mission
        // But assuming the backend handles it or we should use /mission-application/mission/:id if created.
        // For now keeping existing logic or updating to what we built: 
        // We built /mission-application/has-applied/... but maybe not a direct "get by mission" for this view?
        // Wait, the previous code used /jobOffer/${id}/applications. 
        // If that works, fine. If not, we might need to filter manually or use the new route.
        // Let's stick to previous code's logic for fetching applications for now.
        const response = await api.get(`/jobOffer/${id}/applications`);
        setApplications(response.data.data || []);
      } catch (error) {
        console.error("Error fetching applications:", error);
        setApplications([]);
      } finally {
        setApplicationsLoading(false);
      }
    };

    if (id) {
      fetchMission();
      fetchApplications();
    }
  }, [id]);

  const handleEdit = () => {
    navigate(`/dashboard/missions/edit/${id}`);
  };

  const handleDelete = async () => {
    if (window.confirm(t("delete.mission.confirm"))) {
      try {
        setIsDeleting(true);
        await api.delete("/mission/" + id);
        navigate("/missions");
      } catch (error) {
        console.error("Error deleting mission:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleViewApplication = (applicationId) => {
    navigate(`/dashboard/applications/missions/${applicationId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return timeString;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString();
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t("mission.not.found")}
          </h2>
          <button
            onClick={() => navigate("/missions")}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t("backToMissions")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with Back Button and Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 sm:mb-0 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t("back")}
          </button>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {t("mission.edit")}
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {isDeleting ? t("mission.deleting") : t("mission.delete")}
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
          {/* Hero Section with Image and Title */}
          <div className="relative">
            {mission.imageUrl ? (
              <div className="h-96 overflow-hidden">
                <img
                  src={process.env.REACT_APP_BASE_URL + mission.imageUrl}
                  alt={mission.title?.title || mission.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-20"></div>
              </div>
            ) : (
              <div className="h-96 bg-gradient-to-r from-primary-600 to-primary-800"></div>
            )}

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h1 className="text-4xl font-bold mb-2">
                    {mission.title?.title || (typeof mission.title === 'string' ? mission.title : 'Mission Sans Titre')}
                  </h1>

                  <p className="text-sm text-primary-100 mt-2">
                    {t("mission.created")}: {formatDateTime(mission.createdAt)}
                  </p>
                </div>

                {/* Status Badge */}
                <div className="mt-4 sm:mt-0">
                  <span
                    className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${mission.status === "active"
                      ? "bg-emerald-100 text-emerald-800"
                      : mission.status === "completed"
                        ? "bg-primary-100 text-primary-800"
                        : "bg-gray-100 text-gray-800"
                      }`}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {t(`mission.status.${mission.status}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Description */}
            {mission.description && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("mission.description")}
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  {mission.description}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Location */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <MapPin className="w-6 h-6 mr-3 text-primary-600" />
                    {t("mission.location")}
                  </h3>
                  <div className="space-y-2 text-gray-700 dark:text-gray-300">
                    {mission.location?.line1 && (
                      <p className="text-lg">{mission.location.line1}</p>
                    )}
                    {mission.location?.line2 && (
                      <p className="text-lg">{mission.location.line2}</p>
                    )}
                    <p className="text-lg font-medium">
                      {mission.location?.zipCode} {mission.location?.city}
                    </p>
                    {mission.location?.wilaya && (
                      <p className="text-lg">{mission.location.wilaya}</p>
                    )}

                  </div>
                </div>

                {/* Salary */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <DollarSign className="w-6 h-6 mr-3 text-emerald-600" />
                    {t("mission.salary")}
                  </h3>
                  <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                    {mission.salaryHour} DA
                    <span className="text-lg font-normal text-gray-600 dark:text-gray-400 ml-2">
                      / {t("hour")}
                    </span>
                  </div>
                </div>

                {/* Skills */}
                {mission.skills && mission.skills.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Users className="w-6 h-6 mr-3 text-primary-600" />
                      {t("mission.skills")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {mission.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Schedule */}
                {mission.schedule && mission.schedule.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Clock className="w-6 h-6 mr-3 text-primary-600" />
                      {t("mission.schedule")}
                    </h3>
                    <div className="space-y-4">
                      {mission.schedule.map((slot, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800"
                        >
                          <div className="flex items-center mb-2">
                            <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatDate(slot.date)}
                            </span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Requirements */}
                {mission.requirements && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <FileText className="w-6 h-6 mr-3 text-primary-600" />
                      {t("mission.requirements")}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {mission.requirements}
                    </p>
                  </div>
                )}

                {/* Education Level */}
                {mission.educationLevel && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <GraduationCap className="w-6 h-6 mr-3 text-red-600" />
                      {t("mission.education")}
                    </h3>
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      {t(`add.education.levels.${mission.educationLevel}`)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Applications Section */}
            <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-600">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <UserCheck className="w-6 h-6 mr-3 text-primary-600" />
                {t("mission.applications")} ({applications.length})
              </h2>

              {applicationsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-8">
                  <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("no.applications.yet")}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {applications.map((application) => (
                    <div
                      key={application._id}
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            {application.candidate?.name || application.candidate?.firstName + ' ' + application.candidate?.lastName}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {application.candidate?.email}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${application.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : application.status === "accepted"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-red-100 text-red-800"
                            }`}
                        >
                          {t(`application.status.${application.status}`)}
                        </span>
                      </div>

                      {application.coverLetter && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 line-clamp-3">
                          {application.coverLetter}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                        <span>{t("applied")}: {formatDate(application.createdAt)}</span>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewApplication(application._id)}
                          className="flex-1 flex items-center justify-center px-3 py-2 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 transition-colors"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t("view")}
                        </button>
                        {application.candidate?.email && (
                          <button
                            onClick={() => window.open(`mailto:${application.candidate.email}`, '_blank')}
                            className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                        {application.candidate?.phone && (
                          <button
                            onClick={() => window.open(`tel:${application.candidate.phone}`, '_blank')}
                            className="flex items-center justify-center px-3 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 transition-colors"
                          >
                            <Phone className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionDetailsPage;